/* =============================================================
   Motive Kit — Pass 1 (Sept 4, 2026)
   motive-kit.js v1.0
   Load in theme.liquid WITHOUT defer, inside <head>, after
   motive-products.js:
   <script src="{{ 'motive-kit.js' | asset_url }}"></script>

   Kit is Motive's curation workspace. Pass 1 is the manual path:
   - "+ Kit" affordance on product tiles (injected, no snippet edits)
     and on quote-template product pages (above Start a Project).
   - Selected color travels with the product (read from the tile's
     variant-in-title span, or the PDP's COLOR readout).
   - Header nav "Kit" link shows a quiet count when the kit has items.
   - /pages/kit renders the kit as a tile-language grid: image,
     title · color, view, remove.
   - "Start a Project with this Kit" hands the full selection to the
     existing intake by prefilling the notes field (?kit=1). No
     worker changes; the inquiry payload already carries notes.

   Storage: localStorage, key 'motive-kit', array of
   { handle, title, color, image, url }. Deduped by handle+color.
   No accounts, no backend — V1 by design (Playbook Kit doctrine).
   ============================================================= */

(function () {
  'use strict';

  var KEY = 'motive-kit';
  var MOTIVE_KIT_VERSION = 'motive-kit-v1.8';   // v1.8 (Oct 29 2026): the header count is a superscript (Kit¹⁴, like every count on the site) and bumps once when it grows
  window.MOTIVE_KIT_VERSION = MOTIVE_KIT_VERSION;

  // ── STORAGE ─────────────────────────────────────────────────────────────

  function readKit() {
    try {
      var raw = localStorage.getItem(KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }

  function writeKit(items) {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch (e) {}
    updateHeaderCount();
  }

  /* v1.5 — variant-true entries. The correct image and color come from
     the product's own JSON: match the chosen color's variant, or fall
     back to the first variant, and use its featured flat lay. */
  var productCache = {};

  function fetchProduct(handle, cb) {
    if (productCache[handle]) return cb(productCache[handle]);
    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (p) { productCache[handle] = p; cb(p); })
      .catch(function () { cb(null); });
  }

  function colorOptionIndex(p) {
    var opts = (p && p.options) || [];
    for (var i = 0; i < opts.length; i++) {
      var name = typeof opts[i] === 'string' ? opts[i] : ((opts[i] && opts[i].name) || '');
      if (/colou?r/i.test(name)) return i;
    }
    return -1;
  }

  function resolveVariant(handle, color, cb) {
    fetchProduct(handle, function (p) {
      if (!p || !p.variants || !p.variants.length) return cb(null);
      var ci = colorOptionIndex(p);
      var v = null;
      if (color && ci !== -1) {
        for (var i = 0; i < p.variants.length; i++) {
          if ((p.variants[i]['option' + (ci + 1)] || '').toLowerCase() === color.toLowerCase()) {
            v = p.variants[i]; break;
          }
        }
      }
      if (!v) v = p.variants[0];
      cb({
        color: ci !== -1 ? (v['option' + (ci + 1)] || '') : (color || ''),
        image: (v.featured_image && v.featured_image.src) ||
               (p.images && p.images[0]) || ''
      });
    });
  }

  function itemKey(it) {
    return (it.handle || '') + '|' + (it.color || '');
  }

  function inKit(handle, color) {
    var k = handle + '|' + (color || '');
    return readKit().some(function (it) { return itemKey(it) === k; });
  }

  function addToKit(item) {
    var items = readKit();
    var k = itemKey(item);
    if (items.some(function (it) { return itemKey(it) === k; })) return false;
    items.push(item);
    writeKit(items);
    return true;
  }

  function removeFromKit(key) {
    writeKit(readKit().filter(function (it) { return itemKey(it) !== key; }));
  }

  // ── HEADER COUNT ────────────────────────────────────────────────────────

  function kitLinks() {
    return document.querySelectorAll('a[href="/pages/kit"], a[href^="/pages/kit?"]');
  }

  var lastCount = null;
  function updateHeaderCount() {
    var n = readKit().length, grew = lastCount !== null && n > lastCount;
    lastCount = n;
    kitLinks().forEach(function (a) {
      var badge = a.querySelector('.motive-kit-count');
      if (!badge) {
        badge = document.createElement('sup');
        badge.className = 'motive-kit-count';
        (a.querySelector('span') || a).appendChild(badge);   // beside the label, like the menu's other counts
      }
      badge.textContent = n > 0 ? n : '';
      if (grew) {   // one small bump so the eye finds where the item went
        badge.classList.remove('is-bump');
        void badge.offsetWidth;
        badge.classList.add('is-bump');
      }
    });
  }

  // ── ADD AFFORDANCE: TILES ───────────────────────────────────────────────

  /* Tiles are injected, not templated: works on collections, search,
     and the homepage carousel with zero liquid edits. The tile root is
     the nearest ancestor that contains the title span. */

  function tileRoot(el) {
    var node = el;
    while (node && node !== document.body) {
      /* v1.4: Baseline uses a stretched-link tile — the img is NOT a
         descendant of the product anchor, so require title, image, and
         product link as three separate facts. Exactly-one-title guard
         stops the walker from ever over-climbing to the grid. */
      if (node.querySelectorAll &&
          node.querySelectorAll('.motive-tile-title').length === 1 &&
          node.querySelector('img') &&
          node.querySelector('a[href*="/products/"]')) return node;
      node = node.parentElement;
    }
    return null;
  }

  function tileData(root) {
    var link = root.querySelector('a[href*="/products/"]');
    var titleEl = root.querySelector('.motive-tile-title');
    if (!link || !titleEl) return null;
    var href = link.getAttribute('href') || '';
    var m = href.match(/\/products\/([^/?#]+)/);
    if (!m) return null;
    var colorEl = root.querySelector('.motive-tile-title-color');
    var color = colorEl ? colorEl.textContent.replace(/^\s*\u00B7\s*/, '').trim() : '';
    var img = root.querySelector('img[src]');
    return {
      handle: m[1],
      title: titleEl.textContent.trim(),
      color: color,
      image: img ? (img.currentSrc || img.src) : '',
      url: '/products/' + m[1]
    };
  }

  /* v1.7 — tile button is a bare "+" everywhere (aria-label carries
     "Add to Kit"); the flash spells out where it went. */
  function tileLabel() {
    return '+';
  }

  function injectTileButtons() {
    var titles = document.querySelectorAll('.motive-tile-title');
    titles.forEach(function (t) {
      var root = tileRoot(t);
      if (!root || root.querySelector('.motive-kit-add')) return;
      root.classList.add('motive-kit-tile');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'motive-kit-add';
      btn.setAttribute('aria-label', 'Add to Kit');
      btn.textContent = tileLabel();
      root.appendChild(btn);
    });
  }

  // ── ADD AFFORDANCE: QUOTE PDP ───────────────────────────────────────────

  function pdpColor() {
    /* v1.3: the checked variant-picker input IS the selection — its
       value is the color name. Fragment text regex kept as fallback. */
    var checked = document.querySelector('[data-variant-picker] input:checked');
    if (checked && checked.value) return checked.value.trim();
    var frag = document.querySelector('[data-variant-fragment="current-variant"]');
    var text = frag ? frag.textContent : '';
    var m = text.match(/COLOR:\s*([^\n]+)/i);
    return m ? m[1].trim() : '';
  }

  function injectPdpButton() {
    if (!window.location.pathname.includes('/products/')) return;
    var bar = document.querySelector('.motive-sap-bar');
    if (!bar || document.querySelector('.motive-kit-add-pdp')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'motive-kit-add-pdp';
    btn.textContent = 'Add to Kit';
    bar.parentElement.insertBefore(btn, bar);
    btn.addEventListener('click', function () {
      var handle = window.location.pathname.replace('/products/', '').split('?')[0].split('/')[0];
      /* v1.2: on a PDP the first .motive-tile-title in the DOM can
         belong to an unrelated tile fragment — the page h1 is the
         product's actual (display) title. */
      var titleEl = document.querySelector('[data-product-root] h1') ||
                    document.querySelector('h1');
      var slideImg = document.querySelector('.splide--product .splide__slide img[src]') ||
                     document.querySelector('.product-media-container img[src]');
      var item = {
        handle: handle,
        title: titleEl ? titleEl.textContent.trim() : handle,
        color: pdpColor(),
        image: slideImg ? (slideImg.currentSrc || slideImg.src) : '',
        url: '/products/' + handle
      };
      resolveVariant(item.handle, item.color, function (rv) {
        if (rv) {
          if (rv.color) item.color = rv.color;
          if (rv.image) item.image = rv.image;
        }
        var added = addToKit(item);
        flashButton(btn, added ? 'Added to your Kit' : 'Already in your Kit', 'Add to Kit');
      });
    });
  }

  function flashButton(btn, text, restore) {
    var prev = restore || btn.textContent;
    btn.textContent = text;
    btn.classList.add('motive-kit-added');
    setTimeout(function () {
      btn.textContent = prev;
      btn.classList.remove('motive-kit-added');
    }, 1400);
  }

  // ── KIT PAGE ────────────────────────────────────────────────────────────

  function isKitPage() {
    return /^\/pages\/kit\/?$/.test(window.location.pathname);
  }

  function renderKitPage() {
    /* Render into the page's main content area. The Kit page is a plain
       admin page; its main text container gets replaced wholesale. */
    var host = document.querySelector('#MainContent') ||
               document.querySelector('main') ||
               document.body;
    var mount = document.createElement('div');
    mount.className = 'motive-kit-page';

    var items = readKit();

    if (!items.length) {
      mount.innerHTML =
        '<div class="motive-kit-empty">' +
        '<h2>Nothing here yet.</h2>' +
        '<p>Browse the merch and tap + Kit on anything worth seeing together.</p>' +
        '<a class="motive-kit-cta" href="/collections/wear">Browse Merch <span>&rarr;</span></a>' +
        '</div>';
    } else {
      var grid = items.map(function (it) {
        var k = itemKey(it);
        return (
          '<div class="motive-kit-item" data-key="' + k.replace(/"/g, '&quot;') + '">' +
            '<a href="' + it.url + '" class="motive-kit-item-media">' +
              (it.image ? '<img src="' + it.image + '" alt="" loading="lazy">' : '') +
            '</a>' +
            '<div class="motive-kit-item-row">' +
              '<span class="motive-kit-item-title">' + escapeHtml(it.title) +
                (it.color ? '<span class="motive-kit-item-color"> \u00B7 ' + escapeHtml(it.color) + '</span>' : '') +
              '</span>' +
              '<button type="button" class="motive-kit-remove" aria-label="Remove from Kit">Remove</button>' +
            '</div>' +
          '</div>'
        );
      }).join('');

      mount.innerHTML =
        '<div class="motive-kit-grid">' + grid + '</div>' +
        '<div class="motive-kit-actions">' +
          '<a class="motive-kit-cta" href="/pages/start-a-project?kit=1">Start a Project with this Kit <span>&rarr;</span></a>' +
          '<p class="motive-kit-note">Your kit lives in this browser. Send it over and we take it from there.</p>' +
        '</div>';
    }

    /* Replace the page's default content area with the kit UI. */
    var existing = host.querySelector('.motive-kit-page');
    if (existing) existing.remove();
    /* Hide the theme's empty rich-text body if present, keep the title. */
    host.appendChild(mount);

    mount.addEventListener('click', function (e) {
      var btn = e.target.closest('.motive-kit-remove');
      if (!btn) return;
      var itemEl = btn.closest('.motive-kit-item');
      if (!itemEl) return;
      removeFromKit(itemEl.getAttribute('data-key'));
      renderKitPage();
    });
  }

  function escapeHtml(s) {
    return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ── START A PROJECT HANDOFF ─────────────────────────────────────────────

  function prefillIntake() {
    if (!/\/pages\/start-a-project/.test(window.location.pathname)) return;
    var params = new URLSearchParams(window.location.search);
    if (params.get('kit') !== '1') return;
    var items = readKit();
    if (!items.length) return;
    var attempt = 0;
    var wait = setInterval(function () {
      var notes = document.getElementById('miNotes');
      if (notes) {
        clearInterval(wait);
        var lines = items.map(function (it) {
          return it.title + (it.color ? ' \u00B7 ' + it.color : '');
        });
        var summary = 'My Kit: ' + lines.join('; ');
        if (!notes.value.trim()) {
          notes.value = summary;
        } else if (notes.value.indexOf('My Kit:') === -1) {
          notes.value = summary + '\n' + notes.value;
        }
        var productLine = document.getElementById('miProductLine');
        var productTitle = document.getElementById('miProductTitle');
        if (productLine && productTitle && productLine.style.display === 'none') {
          productTitle.textContent = 'Your Kit (' + items.length + ' item' + (items.length !== 1 ? 's' : '') + ')';
          productLine.style.display = 'block';
        }
      } else if (++attempt > 30) {
        clearInterval(wait);
      }
    }, 100);
  }

  // ── EVENTS + INIT ───────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.motive-kit-add');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var root = tileRoot(btn);
    var data = root && tileData(root);
    if (!data) return;
    resolveVariant(data.handle, data.color, function (rv) {
      if (rv) {
        if (rv.color) data.color = rv.color;
        if (rv.image) data.image = rv.image;
      }
      var added = addToKit(data);
      flashButton(btn, added ? 'Added to your Kit' : 'Already in your Kit', tileLabel());
    });
  });

  /* Tiles render and re-render (filters, carousels, island swaps) —
     re-inject on DOM changes, debounced, injection is idempotent. */
  var pending;
  function watchTiles() {
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () {
        injectTileButtons();
        injectPdpButton();
        updateHeaderCount();
      }, 250);
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* Cross-tab sync */
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) {
      updateHeaderCount();
      if (isKitPage()) renderKitPage();
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    injectTileButtons();
    injectPdpButton();
    updateHeaderCount();
    watchTiles();
    if (isKitPage()) renderKitPage();
    prefillIntake();
  });

})();
